// API client and base URL. Imports startSubscriptionRedirect lazily (it lives
// in modals/paywall.js and itself calls into this module for API_BASE — the two
// form a small mutual-dependency that ESM handles via live bindings as long as
// neither calls the other at module-load time).
import { startSubscriptionRedirect } from "../modals/paywall.js";
import { toPence } from "../../../shared/money.js";
import { resolveApiBase } from "./api-base.js";
import { needsIdempotencyKey } from "./keyed-paths.js";
import { state } from "./state.js";
import { companyHeaderFor, pageLostItsCompany } from "./company-header.js";
import { loadFresh, route } from "./navigation.js";
import { showAlertModal } from "../modals/confirm.js";

// The rule lives in lib/api-base.js so it can be tested without a DOM; see the
// comment there for why "localhost means :3000" was not enough.
/** @type {string} Origin (no trailing slash) of the API. */
export const API_BASE = resolveApiBase(window.location);

/**
 * @typedef {object} ApiOptions
 * @property {string}                 [method]      HTTP method (default "GET", or "POST" if body present)
 * @property {BodyInit|object|null}   [body]        Object-as-body will be JSON-stringified by the caller; we set content-type only when it isn't already
 * @property {Record<string,string>}  [headers]     Extra headers; content-type defaults to application/json when body is present and not FormData
 * @property {AbortSignal}            [signal]
 * @property {boolean}                [paywall]     false: a 402 SubscriptionRequired is thrown without starting the billing redirect
 * @property {boolean}                [namesCompany] false: ask as the session stands, naming no company (signing in, and finding out where that left the browser)
 */

/**
 * Wrapped fetch that prefixes API_BASE, always includes credentials, JSON-parses
 * responses, throws on non-2xx, and intercepts 402 SubscriptionRequired to send
 * the user straight to the Stripe billing flow.
 *
 * @param {string}     path            API path beginning with "/"
 * @param {ApiOptions} [options]
 * @returns {Promise<any>} parsed JSON body, or null for 204 No Content
 * @throws  {Error} when the response is not ok (message comes from body.message
 *                  / body.error / fallback "Request failed (status).")
 */
export async function api(path, options = {}) {
  // `paywall: false` hands a 402 back to the caller rather than leaving for
  // billing, for a caller with something to keep first: starting a schedule
  // from compose keeps what was typed as a draft (screens/schedules.js).
  const { paywall = true, namesCompany = true, ...fetchOptions } = options;
  options = { ...fetchOptions, body: onTheWire(fetchOptions.method, path, fetchOptions.body) };
  const hasBody = options.body !== undefined && options.body !== null;
  const isFormData = typeof FormData !== "undefined" && options.body instanceof FormData;
  const headers = {
    ...(options.headers || {})
  };
  if (hasBody && !isFormData && !headers["content-type"] && !headers["Content-Type"]) {
    headers["content-type"] = "application/json";
  }
  // One key per call, and a call is one click: a request that emails a client
  // or moves money can then only happen once however the network treats it
  // (server/idempotency.js).
  if (needsIdempotencyKey(options.method, path) && !headers["Idempotency-Key"]) {
    headers["Idempotency-Key"] = newIdempotencyKey();
  }
  // Everything is asked of the company this page is showing, whichever one
  // another tab has switched the browser's session to since (lib/company-header.js).
  const namedByPage = namesCompany && !headers["Sendinvo-Company"] && Boolean(companyHeaderFor(state.membership?.companyId));
  if (namedByPage) Object.assign(headers, pageCompanyHeaders());

  // headers goes last: spreading options after it replaced the merged headers
  // with the caller's own, dropping content-type from any request that passed
  // a header of its own.
  const res = await fetch(`${API_BASE}${path}`, {
    credentials: "include",
    ...options,
    headers
  });

  if (res.status === 204) return null;
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    if (pageLostItsCompany(res.status, body, namedByPage)) leaveTheCompanyGone(body);
    // 402 SubscriptionRequired → send the user straight to the Stripe billing
    // flow instead of surfacing the raw error. The send/create endpoints
    // return this shape when the company has no active Sendinvo subscription.
    if (paywall && res.status === 402 && body && body.error === "SubscriptionRequired") {
      try {
        startSubscriptionRedirect({ status: body.subscriptionStatus || "none" });
      } catch {
        // If the redirect fails to start for any reason, fall through to the error.
      }
    }
    const err = new Error(body.message || body.error || `Request failed (${res.status}).`);
    err.status = res.status;
    err.body = body;
    throw err;
  }
  return body;
}

// The header naming the company this page is showing, for a request that
// doesn't go through api().
function pageCompanyHeaders() {
  const company = companyHeaderFor(state.membership?.companyId);
  return company ? { "Sendinvo-Company": company } : {};
}

/**
 * A request to the API that isn't JSON, or has to be left to finish as a page
 * closes: credentials, and the company this page is showing, as api() sends.
 * Every other fetch of the API goes through one of these two (test/company-header.test.mjs).
 *
 * @param {string}      path  API path beginning with "/"
 * @param {RequestInit} [init]
 * @returns {Promise<Response>}
 */
export function apiFetch(path, init = {}) {
  return fetch(`${API_BASE}${path}`, {
    credentials: "include",
    ...init,
    headers: { ...pageCompanyHeaders(), ...(init.headers || {}) }
  });
}

// The person was removed from the company this page is showing, or it was
// deleted, since the page loaded. Nothing on it can be asked or saved, so say
// so once and load it afresh: a new page goes by the session, which moves to a
// company they're still in.
let leavingCompanyGone = false;
function leaveTheCompanyGone(body) {
  if (leavingCompanyGone) return;
  leavingCompanyGone = true;
  showAlertModal({ title: body.message || "You’re not a member of this company." })
    .catch(() => {})
    .finally(() => loadFresh(route()));
}

// A file from the API, saved under `fileName`: a PDF, an e-invoice. When the
// server refuses, its own sentence is what's thrown (the demo's says what the
// demo can't do), else `failure`.
export async function downloadFile(path, fileName, failure) {
  const res = await apiFetch(path, { method: "GET" });
  if (!res.ok) {
    const message = await res.json().then((d) => d?.error).catch(() => "");
    throw new Error(message || failure);
  }
  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = objectUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(objectUrl);
}

/**
 * The If-Match header for a write based on a row's version (server/row-version.js),
 * or no header when there isn't one — the demo's rows carry none.
 *
 * @param {number|null|undefined} version
 * @returns {Record<string,string>}
 */
export function ifMatch(version) {
  return Number.isInteger(version) ? { "If-Match": `"${version}"` } : {};
}

function newIdempotencyKey() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return crypto.randomUUID();
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
}

// ── The request body's wire shape (iOS map, ticket 06, answers 4 and 6) ────
// Every amount also goes as integer pence, named …Pence, beside the pounds
// fields — the server prefers pence (server/money-input.js), and an API or demo
// that predates it still reads pounds. And a new document or client names its
// own id (server/client-id.js). Done here, once, so no caller can miss it; the
// pounds fields go when every client sends pence.

const DOCUMENT_BODY = /^\/(invoices\/draft|invoices\/[^/?#]+|quotes\/draft|quotes\/[^/?#]+)(?:[?#]|$)/;
const ITEMS_BODY = /^\/credit-notes(?:[?#]|$)/;
const SCHEDULE_BODY = /^\/schedules(?:\/[^/?#]+)?(?:[?#]|$)/;
const AMOUNT_BODY = /^\/invoices\/[^/?#]+\/(record-payment|record-refund)(?:[?#]|$)/;
const CREATES = /^\/(invoices\/draft|quotes\/draft|credit-notes|schedules|contacts)(?:[?#]|$)/;

function penceOf(value) {
  if (value === undefined || value === null || String(value).trim() === "") return undefined;
  const n = Number.parseFloat(String(value));
  return Number.isFinite(n) && n >= 0 ? toPence(n) : undefined;
}

function addPence(target, poundsField, penceField) {
  if (!target || typeof target !== "object" || penceField in target) return;
  const pence = penceOf(target[poundsField]);
  if (pence !== undefined) target[penceField] = pence;
}

function newId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return crypto.randomUUID();
  const b = crypto.getRandomValues(new Uint8Array(16));
  b[6] = (b[6] & 0x0f) | 0x40;
  b[8] = (b[8] & 0x3f) | 0x80;
  const hex = Array.from(b, (x) => x.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

function onTheWire(method, path, body) {
  const verb = String(method || "GET").toUpperCase();
  if ((verb !== "POST" && verb !== "PUT") || typeof body !== "string") return body;
  const route = String(path || "");
  // Anchored at the end, so only a draft create or edit: /invoices/:id/send
  // and every other action under a document doesn't match.
  const document = verb === "POST" && DOCUMENT_BODY.test(route);
  const items = verb === "POST" && ITEMS_BODY.test(route);
  const schedule = SCHEDULE_BODY.test(route) && (verb === "POST" ? !/\/schedules\/[^/?#]+/.test(route) : true);
  const amount = verb === "POST" && AMOUNT_BODY.test(route);
  const create = verb === "POST" && CREATES.test(route);
  if (!document && !items && !schedule && !amount && !create) return body;

  let parsed;
  try { parsed = JSON.parse(body); } catch { return body; }
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return body;

  if (document || items) {
    (Array.isArray(parsed.items) ? parsed.items : []).forEach((item) => addPence(item, "unitPrice", "unitPricePence"));
  }
  if (document) {
    addPence(parsed, "cisLabourAmount", "cisLabourAmountPence");
    if (String(parsed.discountType || "").toLowerCase() === "fixed") addPence(parsed, "discountValue", "discountValuePence");
    if (String(parsed.depositType || "").toLowerCase() === "fixed") addPence(parsed, "depositValue", "depositValuePence");
  }
  if (schedule) {
    (Array.isArray(parsed.lineItems) ? parsed.lineItems : []).forEach((item) => addPence(item, "unitPrice", "unitPricePence"));
  }
  if (amount) addPence(parsed, "amount", "amountPence");
  if (create && !parsed.id) parsed.id = newId();
  return JSON.stringify(parsed);
}
